// src/budget/data/grandList/utils/grandList.js

import { useState, useEffect } from "react";
import { COLLECTION_MAP } from "../../../shared/constants";

/**
 * Fetches the Net Grand List values by fiscal year for a CT municipality.
 *
 * @param {string} town - The normalized town key (e.g., "glastonbury")
 * @returns {Object} { data, loading, error }
 */
export const useGrandListHistory = (town) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!town) {
      setError("No town specified");
      setLoading(false);
      return;
    }

    const normalizedKey = town.toLowerCase().replace(/[\s,().'-]/g, "");
    const mappedTown = COLLECTION_MAP[normalizedKey];

    if (!mappedTown) {
      setError("Invalid or unmapped town");
      setLoading(false);
      return;
    }

    const fetchGrandList = async () => {
      setLoading(true);
      setError(null);

      try {
        const encodedTown = encodeURIComponent(mappedTown);
        const url = `https://data.ct.gov/resource/webp-fgt3.json?town_name=${encodedTown}&$order=year DESC`;

        const res = await fetch(url);
        if (!res.ok) throw new Error(`Request failed with status ${res.status}`);

        const json = await res.json();

        const cleaned = json.map((row) => ({
          fiscalYear: row.year,
          townName: row.town_name,
          residential: Number(row.residential || 0),
          commercial: Number(row.commercial || 0),
          industrial: Number(row.industrial || 0),
          publicUtility: Number(row.public_utility || 0),
          vacantLand: Number(row.vacant_land || 0),
          landUse: Number(row.land_use || 0),
          apartment: Number(row.apartment || 0),
          totalRealProperty: Number(row.total_real_property || 0),
          netRealProperty: Number(row.net_real_property || 0),
          motorVehicle: Number(row.motor_vehicle || 0),
          totalNetMotorVehicle: Number(row.total_net_motor_vehicle || 0),
          personalProperty: Number(row.personal_property || 0),
          netPersonalProperty: Number(row.net_personal_property || 0),
          totalNetGrandList: Number(row.total_net_grand_list || 0),
        }));
        
        setData(cleaned);
      } catch (err) {
        console.error("Grand List fetch error:", err);
        setError(err.message || "Unknown error");
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchGrandList();
  }, [town]);

  return { data, loading, error };
};
